import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { NumberInput } from './NumberInput.js';
import { SearchSelect } from './SearchSelect.js';
import { SkuCodeInput, SkuNameInput } from './SkuCodeInput.js';

const { t } = i18n;

/**
 * Product configurator (WMS-46) — find a product by the components it is built
 * from, rather than by its name.
 *
 * Partners kept creating products that already existed under a different name,
 * because a name is only findable if you already know it. Here each component
 * added narrows the catalogue; the count is shown from the first component so
 * the funnel is visible, while the list itself waits for the second, where it
 * becomes short enough to read. When the funnel empties, creating a new product
 * is the remaining option — and that create is gated on a duplicate check.
 */

interface ComponentRow {
  id: string;
  code: string;
  type: 'PRODUCT' | 'COMPONENT';
  nameLt: string;
  nameEn: string | null;
  photoUrl: string | null;
  availableQty: number;
  maxProducibleQty: number | null;
}

interface MatchedBomLine {
  componentSkuId: string;
  code: string;
  nameLt: string;
  qtyRequired: number;
}

interface MatchedProduct {
  id: string;
  code: string;
  nameLt: string;
  nameEn: string | null;
  photoUrl: string | null;
  extraComponentCount: number;
  bomLines: MatchedBomLine[];
}

interface MatchResponse {
  totalMatches: number;
  results: MatchedProduct[];
}

interface DuplicateSummary {
  id: string;
  code: string;
  nameLt: string;
}

interface DuplicateResponse {
  exact: DuplicateSummary[];
  sameSetDifferentQty: DuplicateSummary[];
}

interface SelectionRow {
  componentSkuId: string;
  qtyRequired: number;
}

export function ProductConfigurator({
  partnerId,
  canCreate,
  onClose,
  onPick,
}: {
  partnerId: string;
  /** `sku.write` in rbac.matrix.ts — ADMIN and PARTNER_ADMIN only. */
  canCreate: boolean;
  onClose: () => void;
  /**
   * Supplied by the order-line flow: picking a result drops it into the line
   * being edited. Omitted in the catalogue flow, where a result is opened
   * instead.
   */
  onPick?: (product: { id: string; code: string; nameLt: string }) => void;
}) {
  const navigate = useNavigate();
  const [components, setComponents] = useState<ComponentRow[]>([]);
  const [selection, setSelection] = useState<SelectionRow[]>([]);
  const [match, setMatch] = useState<MatchResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState('');
  const [nameLt, setNameLt] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [duplicates, setDuplicates] = useState<DuplicateResponse | null>(null);
  const [overrideExact, setOverrideExact] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cloned, setCloned] = useState<{ id: string; code: string } | null>(null);

  useEffect(() => {
    api<ComponentRow[]>(`/partners/${partnerId}/skus`)
      .then((all) => setComponents(all.filter((s) => s.type === 'COMPONENT')))
      .catch(() => setComponents([]));
  }, [partnerId]);

  /** Rows the user has actually filled in — a freshly added row is blank. */
  const validSelection = useMemo(
    () => selection.filter((s) => s.componentSkuId !== '' && s.qtyRequired > 0),
    [selection],
  );
  const distinctComponents = useMemo(
    () => new Set(validSelection.map((s) => s.componentSkuId)).size,
    [validSelection],
  );

  // Debounced search with a generation guard, like the orders search (WMS-34):
  // the quantity spinner fires a request per keystroke, and without the guard a
  // slow early response can land after a fast later one and show stale rows.
  const selectionKey = JSON.stringify(validSelection);
  const selectionKeyRef = useRef(selectionKey);
  selectionKeyRef.current = selectionKey;
  useEffect(() => {
    if (validSelection.length === 0) {
      setMatch(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setMatch(null);
    setSearching(true);
    const timer = setTimeout(() => {
      api<MatchResponse>(`/partners/${partnerId}/skus/match`, {
        method: 'POST',
        body: { selection: validSelection },
      })
        .then((res) => {
          if (cancelled) return;
          setMatch(res);
          setError(null);
        })
        .catch(() => {
          if (!cancelled) {
            setMatch(null);
            setError(t('common.error'));
          }
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `selectionKey` is the serialized selection — depending on the array
    // itself would re-fire on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, selectionKey]);

  // Any change to the recipe invalidates a duplicate verdict and the override
  // it was granted for — otherwise a confirmation given for recipe A silently
  // authorises creating recipe B. `creating` has to close with them: leaving
  // the form open with a cleared verdict makes `blockedByExact` false and would
  // hand the user a Create button for a recipe nothing has checked.
  useEffect(() => {
    setCreating(false);
    setDuplicates(null);
    setOverrideExact(false);
  }, [selectionKey]);

  function addRow() {
    setSelection((prev) => [...prev, { componentSkuId: '', qtyRequired: 1 }]);
  }

  async function clone(productSkuId: string) {
    // Open synchronously from the click so browser popup blockers allow it;
    // navigate only after the API has created the clone (WMS-49).
    const cloneTab = window.open('about:blank', '_blank');
    if (cloneTab) cloneTab.opener = null;
    setBusy(true);
    setError(null);
    try {
      const created = await api<{ id: string; code: string }>(
        `/partners/${partnerId}/skus/${productSkuId}/clone`,
        { method: 'POST' },
      );
      setCloned({ id: created.id, code: created.code });
      const url = `/partners/${partnerId}/skus/${created.id}`;
      if (cloneTab) {
        cloneTab.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      cloneTab?.close();
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  /** Step 1 of creating: ask the server whether this recipe already exists. */
  async function startCreate() {
    const requestedSelectionKey = selectionKey;
    setBusy(true);
    setError(null);
    try {
      const found = await api<DuplicateResponse>(`/partners/${partnerId}/skus/duplicate-check`, {
        method: 'POST',
        body: { selection: validSelection },
      });
      if (selectionKeyRef.current !== requestedSelectionKey) return;
      setDuplicates(found);
      setCreating(true);
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  /** Step 2: the server re-checks and creates the product + BOM atomically. */
  async function create() {
    setBusy(true);
    setError(null);
    try {
      const created = await api<{ id: string }>(`/partners/${partnerId}/skus/configured-product`, {
        method: 'POST',
        body: {
          code,
          nameLt,
          nameEn: nameEn || undefined,
          selection: validSelection,
          allowExactDuplicate: overrideExact,
        },
      });
      if (onPick) {
        onPick({ id: created.id, code, nameLt });
        return;
      }
      navigate(`/partners/${partnerId}/skus/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'sku_code_taken') {
        setError(t('sku.create.code_taken'));
      } else if (err instanceof ApiError && err.code === 'sku_exact_duplicate') {
        setCreating(false);
        setDuplicates(null);
        setOverrideExact(false);
        setError(t('configurator.create.exact_duplicate'));
      } else {
        setError(t('common.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  const componentOptions = (rowIndex: number) => {
    const takenElsewhere = new Set(
      selection.filter((_, i) => i !== rowIndex).map((s) => s.componentSkuId),
    );
    return components
      .filter((c) => !takenElsewhere.has(c.id))
      .map((c) => ({
        id: c.id,
        label: `${c.code} — ${c.nameLt}`,
        keywords: c.nameEn ?? undefined,
        imageUrl: c.photoUrl,
        availableQty: c.availableQty,
        maxProducibleQty: c.maxProducibleQty ?? undefined,
      }));
  };

  const blockedByExact = (duplicates?.exact.length ?? 0) > 0 && !overrideExact;
  const results = match?.results ?? [];

  return (
    <Dialog title={t('configurator.title')} onClose={onClose} size="xl" height="tall">
      <div className="space-y-4 text-sm">
        <p className="text-xs text-muted-foreground">{t('configurator.intro')}</p>

        {components.length === 0 ? (
          <div className="rounded border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
            {t('bom.no_components')}
          </div>
        ) : null}

        {/* ---- the selection ---- */}
        <div className="space-y-2">
          {selection.map((row, i) => (
            <div key={i} className="flex items-center gap-2 rounded border border-border p-2">
              <SearchSelect
                className="min-w-0 flex-1"
                menuSize="tall"
                ariaLabel={t('bom.pick_component')}
                value={row.componentSkuId}
                onChange={(id) =>
                  setSelection((prev) =>
                    prev.map((r, j) => (j === i ? { ...r, componentSkuId: id } : r)),
                  )
                }
                options={componentOptions(i)}
              />
              <NumberInput
                min={0.0001}
                step={0.0001}
                value={row.qtyRequired}
                onChange={(v) =>
                  setSelection((prev) =>
                    prev.map((r, j) => (j === i ? { ...r, qtyRequired: v ?? 0 } : r)),
                  )
                }
                className="w-24 shrink-0 rounded border border-border bg-background px-2 py-1 tabular-nums"
              />
              <button
                type="button"
                aria-label={t('configurator.remove_component')}
                onClick={() => setSelection((prev) => prev.filter((_, j) => j !== i))}
                className="shrink-0 rounded border border-border px-2 py-1 text-xs"
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addRow}
            disabled={components.length === 0}
            className="rounded border border-border px-3 py-1.5 disabled:opacity-50"
          >
            {t('bom.add_line')}
          </button>
        </div>

        {/* ---- the funnel counter: shown from the very first component ---- */}
        {validSelection.length > 0 ? (
          <div className="rounded border border-border bg-muted/40 px-3 py-2">
            {t('configurator.matches')}:{' '}
            <strong className="tabular-nums">{searching ? '…' : (match?.totalMatches ?? 0)}</strong>
          </div>
        ) : null}

        {error ? <div className="text-red-600">{error}</div> : null}

        {cloned ? (
          <div className="rounded border border-border bg-muted/40 px-3 py-2">
            {t('configurator.cloned')}: <span className="font-mono">{cloned.code}</span>{' '}
            {/* Opened in a new tab so an order draft in the background survives. */}
            <a
              href={`/partners/${partnerId}/skus/${cloned.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              {t('configurator.open_clone')}
            </a>
          </div>
        ) : null}

        {/* ---- results ---- */}
        {validSelection.length > 0 && distinctComponents < 2 ? (
          <div className="rounded border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
            {t('configurator.need_two')}
          </div>
        ) : null}

        {distinctComponents >= 2 && !searching ? (
          results.length === 0 ? (
            <div className="rounded border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
              {t('configurator.no_results')}
            </div>
          ) : (
            <ul className="space-y-2">
              {results.map((p) => (
                <li key={p.id} className="flex items-start gap-3 rounded border border-border p-2">
                  {p.photoUrl ? (
                    <img
                      src={p.photoUrl}
                      alt=""
                      className="h-12 w-12 shrink-0 rounded border border-border object-cover"
                    />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs">{p.code}</span>
                      <span className="font-medium">{p.nameLt}</span>
                      {p.extraComponentCount > 0 ? (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                          +{p.extraComponentCount} {t('configurator.extra_components')}
                        </span>
                      ) : (
                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100">
                          {t('configurator.exact_match')}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {p.bomLines.map((l) => `${l.nameLt} × ${l.qtyRequired}`).join(', ')}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col gap-1">
                    {onPick ? (
                      <button
                        type="button"
                        onClick={() => onPick({ id: p.id, code: p.code, nameLt: p.nameLt })}
                        className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground"
                      >
                        {t('configurator.choose')}
                      </button>
                    ) : (
                      <Link
                        to={`/partners/${partnerId}/skus/${p.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded border border-border px-2 py-1 text-center text-xs"
                      >
                        {t('configurator.open')}
                      </Link>
                    )}
                    {canCreate ? (
                      <button
                        type="button"
                        onClick={() => void clone(p.id)}
                        disabled={busy}
                        className="rounded border border-border px-2 py-1 text-xs disabled:opacity-50"
                      >
                        {t('configurator.clone')}
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )
        ) : null}

        {/* ---- create a new product from the configured components ---- */}
        {distinctComponents >= 2 ? (
          <div className="space-y-2 rounded border border-border p-3">
            {!canCreate ? (
              // `sku.write` excludes ORDER_PLACER (rbac.matrix.ts). They can
              // search, but the catalogue is shaped by the partner admin.
              <p className="text-xs text-muted-foreground">{t('configurator.cannot_create')}</p>
            ) : !creating ? (
              <button
                type="button"
                onClick={() => void startCreate()}
                disabled={busy}
                className="rounded border border-border px-3 py-1.5 disabled:opacity-50"
              >
                {t('configurator.create.action')}
              </button>
            ) : (
              <div className="space-y-3">
                {blockedByExact ? (
                  <div className="space-y-2 rounded border border-red-400 bg-red-50 p-3 text-red-900 dark:bg-red-950/40 dark:text-red-100">
                    <p className="font-medium">{t('configurator.duplicate.exact')}</p>
                    <ul className="list-inside list-disc text-sm">
                      {duplicates?.exact.map((d) => (
                        <li key={d.id}>
                          <span className="font-mono text-xs">{d.code}</span> — {d.nameLt}
                        </li>
                      ))}
                    </ul>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setCreating(false)}
                        className="rounded border border-border bg-background px-3 py-1.5 text-foreground"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setOverrideExact(true)}
                        className="rounded bg-red-600 px-3 py-1.5 text-white"
                      >
                        {t('configurator.duplicate.create_anyway')}
                      </button>
                    </div>
                  </div>
                ) : null}

                {(duplicates?.sameSetDifferentQty.length ?? 0) > 0 ? (
                  <div className="rounded border border-amber-400 bg-amber-50 p-3 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                    <p>{t('configurator.duplicate.same_set')}</p>
                    <ul className="list-inside list-disc text-sm">
                      {duplicates?.sameSetDifferentQty.map((d) => (
                        <li key={d.id}>
                          <span className="font-mono text-xs">{d.code}</span> — {d.nameLt}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {!blockedByExact ? (
                  <div className="space-y-2">
                    <label className="block space-y-1">
                      <span className="font-medium">{t('sku.col.code')}</span>
                      <SkuCodeInput
                        partnerId={partnerId}
                        value={code}
                        onChange={setCode}
                        required
                        className="w-full rounded border border-border bg-background px-2 py-1.5 font-mono"
                      />
                    </label>
                    <label className="block space-y-1">
                      <span className="font-medium">{t('sku.col.name_lt')}</span>
                      <SkuNameInput
                        partnerId={partnerId}
                        value={nameLt}
                        onChange={setNameLt}
                        required
                        className="w-full rounded border border-border bg-background px-2 py-1.5"
                      />
                    </label>
                    <label className="block space-y-1">
                      <span className="font-medium">{t('sku.col.name_en')}</span>
                      <input
                        value={nameEn}
                        onChange={(e) => setNameEn(e.target.value)}
                        className="w-full rounded border border-border bg-background px-2 py-1.5"
                      />
                    </label>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setCreating(false)}
                        className="rounded border border-border px-3 py-1.5"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void create()}
                        disabled={busy || !code || !nameLt}
                        className="rounded bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-50"
                      >
                        {busy ? t('common.loading') : t('configurator.create.submit')}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ) : null}

        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
